/**
 * Note di analisi di un Ticket: non vanno mai perse tra gli stadi del workflow.
 *
 *  - La bozza vive nel dettaglio del ticket (`useNoteAnalista`): le azioni di workflow (Prendi in
 *    carico, Abbandona, Chiudi, Promuovi) chiamano `salvaSeModificate()` PRIMA di cambiare stato,
 *    così un testo scritto e non ancora salvato non si perde quando il ticket cambia sezione.
 *  - Il salvataggio invia `expectedNotes` (le note su cui si è iniziato a scrivere): se nel
 *    frattempo un altro utente le ha cambiate, il server risponde 409 e qui si mostra la versione
 *    salvata, da unire al proprio testo, invece di sovrascriverla.
 *  - Finché la bozza non è modificata segue il valore del server (refetch dopo le mutazioni).
 */
import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client.js';
import { useUpdateTicketNotes } from './hooks.js';
import type { Ticket } from './types.js';
import styles from './Triage.module.css';

export interface NoteAnalistaState {
  testo: string;
  setTesto: (v: string) => void;
  /** Bozza diversa dalle note salvate su cui è basata. */
  modificate: boolean;
  /** Il server ha rifiutato il salvataggio: le note sono cambiate nel frattempo. */
  conflitto: boolean;
  salvataggio: ReturnType<typeof useUpdateTicketNotes>;
  /** Salva la bozza; true se salvata (o niente da salvare), false se conflitto/errore. */
  salvaSeModificate: () => Promise<boolean>;
  /** Unisce la versione salvata da altri (in testa) al proprio testo e chiude il conflitto. */
  unisci: () => void;
  /** Scarta il proprio testo e riparte dalla versione salvata. */
  scarta: () => void;
}

export function useNoteAnalista(triage: Ticket | undefined, triageId: number): NoteAnalistaState {
  const server = triage?.payload.analystNotes ?? '';
  const [testo, setTesto] = useState(server);
  const [base, setBase] = useState(server);
  const [conflitto, setConflitto] = useState(false);
  const salvataggio = useUpdateTicketNotes(triageId);
  const modificate = testo !== base;

  // Bozza non modificata → segue il server (es. note salvate da un altro utente, refetch).
  useEffect(() => {
    if (!modificate && !conflitto) {
      setTesto(server);
      setBase(server);
    }
  }, [server]);

  async function salvaSeModificate(): Promise<boolean> {
    if (!modificate && !conflitto) return true;
    if (conflitto) return false;
    try {
      await salvataggio.mutateAsync({ analystNotes: testo, expectedNotes: base });
      setBase(testo);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setConflitto(true);
      return false;
    }
  }

  return {
    testo,
    setTesto,
    modificate,
    conflitto,
    salvataggio,
    salvaSeModificate,
    unisci: () => {
      setTesto(server && testo ? `${server}\n\n${testo}` : server || testo);
      setBase(server);
      setConflitto(false);
      salvataggio.reset();
    },
    scarta: () => {
      setTesto(server);
      setBase(server);
      setConflitto(false);
      salvataggio.reset();
    },
  };
}

function messaggioErrore(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : 'Errore sconosciuto';
}

/** Form delle note (modificabile): la bozza arriva da `useNoteAnalista` del dettaglio. */
export function NoteAnalistaForm({ triage, note }: { triage: Ticket; note: NoteAnalistaState }) {
  const { salvataggio } = note;
  const server = triage.payload.analystNotes ?? '';

  function salva(e: React.FormEvent) {
    e.preventDefault();
    void note.salvaSeModificate();
  }

  return (
    <form className={styles.formCard} onSubmit={salva} data-testid={`note-triage-${triage.id}`}>
      <p className={styles.formTitle}>Note di analisi</p>
      <p className={styles.formHint}>
        Restano sul ticket in ogni passaggio (presa in carico, abbandono, chiusura, riapertura) e
        alla promozione vengono riportate sul report ENISA.
      </p>
      <div className={styles.formGroup}>
        <textarea
          className={styles.formTextarea}
          value={note.testo}
          onChange={(e) => note.setTesto(e.target.value)}
          placeholder="Appunti dell'analista sulla release / sulle CVE…"
          data-testid={`textarea-note-triage-${triage.id}`}
        />
      </div>
      {note.conflitto && (
        <div
          className={styles.dialogWarning}
          role="alert"
          data-testid={`conflitto-note-${triage.id}`}
        >
          <p style={{ margin: '0 0 0.4rem' }}>
            Nel frattempo un altro utente ha modificato le note: il tuo testo <strong>non</strong> è
            stato salvato. Versione salvata:
          </p>
          <pre
            style={{ whiteSpace: 'pre-wrap', margin: '0 0 0.5rem' }}
            data-testid={`note-salvate-${triage.id}`}
          >
            {server || '(vuote)'}
          </pre>
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={note.unisci}
              data-testid={`btn-unisci-note-${triage.id}`}
            >
              Unisci al mio testo
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={note.scarta}
              data-testid={`btn-scarta-note-${triage.id}`}
            >
              Scarta il mio testo
            </button>
          </div>
        </div>
      )}
      <div className={styles.formActions}>
        <button
          type="submit"
          className={styles.btnSecondary}
          disabled={salvataggio.isPending || note.conflitto}
          data-testid={`btn-salva-note-triage-${triage.id}`}
        >
          {salvataggio.isPending ? 'Salvataggio…' : 'Salva note'}
        </button>
        {note.modificate && !note.conflitto && !salvataggio.isPending && (
          <span className={styles.formHint} data-testid={`note-non-salvate-${triage.id}`}>
            Modifiche non salvate (vengono salvate anche prima di prendere in carico, abbandonare,
            chiudere o promuovere).
          </span>
        )}
        {salvataggio.isError && !note.conflitto && (
          <span className={styles.inlineMutError} data-testid={`errore-note-triage-${triage.id}`}>
            {messaggioErrore(salvataggio.error)}
          </span>
        )}
        {salvataggio.isSuccess && !note.modificate && (
          <span className={styles.successMsg} data-testid={`successo-note-triage-${triage.id}`}>
            Note salvate.
          </span>
        )}
      </div>
    </form>
  );
}
